import {
  ArrowRight,
  BookOpenText,
  CheckCircle2,
  LifeBuoy,
  MessageSquarePlus,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Ticket,
  Zap,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useSession } from "../../context/SessionContext";

const GUIDES = [
  {
    title: "How platform connection setup works",
    copy: "Understand the supported setup for your source and target platforms.",
    href: "https://docs.next-cart.com/connect-your-source-and-target-platforms/",
    icon: CheckCircle2,
  },
  {
    title: "Configure additional options",
    copy: "Review source, target, URL, notification, and reporting options before migration.",
    href: "https://docs.next-cart.com/configure-your-migration/configure-additional-options/",
    icon: Settings2,
  },
  {
    title: "Troubleshoot connection issues",
    copy: "Diagnose failed, unstable, or incorrect platform connections.",
    href: "https://docs.next-cart.com/troubleshooting/connection-issues/",
    icon: Zap,
  },
];

export function HomePage() {
  const { status, user, customerLoginPending, startCustomerLogin } = useSession();
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  function search(event: FormEvent) {
    event.preventDefault();
    if (query.trim()) navigate(`/new?subject=${encodeURIComponent(query.trim())}`);
  }

  return (
    <>
      <section className="support-hero">
        <div className="support-hero__glow" aria-hidden="true" />
        <div className="support-hero__content">
          <span className="hero-pill"><Sparkles aria-hidden="true" size={15} /> Support built around your migration</span>
          <h1>{status === "authenticated" && user ? `How can we help, ${user.name.split(" ")[0]}?` : "How can we help?"}</h1>
          <p>Find a quick answer or send our migration specialists everything they need in one place.</p>
          <form className="hero-search" onSubmit={search} role="search">
            <Search aria-hidden="true" size={20} />
            <label className="sr-only" htmlFor="help-search">Describe what you need help with</label>
            <input id="help-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="e.g. Product images are missing" />
            <button className="button button--primary" type="submit">Get help</button>
          </form>
          <div className="hero-quick-links">
            <span>Popular:</span>
            <button type="button" onClick={() => setQuery("Migration status")}>migration status</button>
            <button type="button" onClick={() => setQuery("Missing images")}>missing images</button>
            <button type="button" onClick={() => setQuery("Recent data migration")}>recent data</button>
          </div>
        </div>
      </section>

      <section className="home-actions" aria-labelledby="home-actions-title">
        <div className="section-heading">
          <div><p className="eyebrow">Support center</p><h2 id="home-actions-title">Start where you need</h2></div>
        </div>
        <div className="action-card-grid">
          <article className="action-card action-card--primary">
            <span className="action-card__icon"><MessageSquarePlus aria-hidden="true" /></span>
            <div><h3>Ask our team</h3><p>Share the issue, migration ID, and files. We will route it to the right specialist.</p></div>
            <Link className="text-link" to="/new">Create a ticket <ArrowRight aria-hidden="true" size={16} /></Link>
          </article>
          <article className="action-card">
            <span className="action-card__icon"><Ticket aria-hidden="true" /></span>
            <div><h3>Track your requests</h3><p>See every reply and update without searching through your inbox.</p></div>
            {status === "authenticated" ? (
              <Link className="text-link" to="/tickets">View my tickets <ArrowRight aria-hidden="true" size={16} /></Link>
            ) : (
              <button className="text-link" type="button" disabled={customerLoginPending || status === "loading"} onClick={startCustomerLogin}>{customerLoginPending ? "Signing in…" : "Sign in to view"} <ArrowRight aria-hidden="true" size={16} /></button>
            )}
          </article>
          <article className="action-card">
            <span className="action-card__icon"><BookOpenText aria-hidden="true" /></span>
            <div><h3>Explore Next-Cart Docs</h3><p>Step-by-step guidance for connecting stores, configuring migrations, validating results, and troubleshooting.</p></div>
            <a className="text-link" href="https://docs.next-cart.com/" target="_blank" rel="noreferrer">Browse documentation <ArrowRight aria-hidden="true" size={16} /></a>
          </article>
        </div>
      </section>

      <section className="guide-section" aria-labelledby="guide-title">
        <div className="section-heading">
          <div><p className="eyebrow">From Next-Cart Docs</p><h2 id="guide-title">Recommended documentation</h2></div>
          <a className="text-link desktop-only" href="https://docs.next-cart.com/" target="_blank" rel="noreferrer">View all documentation <ArrowRight aria-hidden="true" size={16} /></a>
        </div>
        <div className="guide-list">
          {GUIDES.map(({ title, copy, href, icon: Icon }) => (
            <a key={title} href={href} target="_blank" rel="noreferrer">
              <span><Icon aria-hidden="true" size={20} /></span>
              <div><h3>{title}</h3><p>{copy}</p></div>
              <ArrowRight aria-hidden="true" size={18} />
            </a>
          ))}
        </div>
      </section>

      <section className="trust-strip" aria-label="Support promises">
        <div><ShieldCheck aria-hidden="true" /><span><strong>Secure by design</strong>Your account and attachments stay protected.</span></div>
        <div><LifeBuoy aria-hidden="true" /><span><strong>Migration specialists</strong>Your request reaches a team that knows your context.</span></div>
        <div><Zap aria-hidden="true" /><span><strong>Everything connected</strong>Migration details travel with your ticket.</span></div>
      </section>
    </>
  );
}
